import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Table, Tag, Space, Badge, Spin, Empty,
  Alert, Tooltip, Row, Col, Button, Collapse, ConfigProvider, theme,
} from 'antd';
import {
  ThunderboltOutlined, ReloadOutlined, WifiOutlined,
  DisconnectOutlined, ClockCircleOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { API_BASE_URL } from '../api';
import { compareLiveDayLabels } from '../utils/compareLiveDayLabels';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const DARK_BG = 'var(--surface-page)';
const DARK_SURFACE = 'var(--surface-card)';
const DARK_BORDER = 'var(--border-subtle)';

const POLL_INTERVAL_MS = 15_000;

/** Safely parse wind (may be string from API) and return numeric value or null if invalid. */
function parseWind(wind: string | number | null | undefined): number | null {
  const n = typeof wind === 'number' ? wind : parseFloat(String(wind ?? ''));
  return !isNaN(n) ? n : null;
}

interface LiveCompetition {
  id: number;
  name: string;
  session_no: string | null;
  competition_date: string | null;
  is_active: boolean;
  agent_online: boolean;
}

interface StartlistEntry {
  lane: number;
  bib: string | null;
  last_name: string | null;
  first_name: string | null;
  affiliation: string | null;
}

interface ResultEntry {
  place: number | null;
  lane: number;
  bib: string | null;
  last_name: string | null;
  first_name: string | null;
  affiliation: string | null;
  time: string | null;
  wind: number | null;
  delta_time: string | null;
}

interface LiveEvent {
  id: number;
  event_no: string;
  event_name: string | null;
  round_label: string | null;
  heat_number: number;
  event_date: string | null;
  programme_order: number | null;
  startlist: StartlistEntry[];
  results: ResultEntry[];
}

type EventMap = Record<number, { events: LiveEvent[]; lastUpdated: Date | null }>;

interface EventGroup {
  event_no: string;
  event_name: string;
  programme_order: number | null;
  heats: LiveEvent[];
}

/** Group flat event list into Day → EventGroup → heats, preserving programme order. */
function groupByDayAndEvent(events: LiveEvent[]): [string, EventGroup[]][] {
  const dayOrder: string[] = [];
  const dayMap: Record<string, Record<string, EventGroup>> = {};

  for (const ev of events) {
    const day = ev.event_date ?? 'Unknown date';
    const groupKey = `${ev.event_no}||${ev.event_name ?? ''}`;

    if (!dayMap[day]) { dayMap[day] = {}; dayOrder.push(day); }
    if (!dayMap[day][groupKey]) {
      dayMap[day][groupKey] = {
        event_no: ev.event_no,
        event_name: ev.event_name ?? '',
        programme_order: ev.programme_order ?? null,
        heats: [],
      };
    }
    dayMap[day][groupKey].heats.push(ev);
  }

  // ISO YYYY-MM-DD sorts lexicographically; Session/Day N need numeric order
  dayOrder.sort(compareLiveDayLabels);

  return dayOrder.map((day) => {
    const groups = Object.values(dayMap[day]).sort((a, b) => {
      const pa = a.programme_order ?? 9999;
      const pb = b.programme_order ?? 9999;
      return pa - pb;
    });
    // Sort heats within each group by heat_number ascending
    groups.forEach(g => g.heats.sort((a, b) => a.heat_number - b.heat_number));
    return [day, groups];
  });
}

function formatDayLabel(dateStr: string): string {
  if (dateStr === 'Unknown date') return dateStr;
  // If it's already a named label like "Day 1", return as-is
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatRelativeTime(d: Date | null, nowMs: number): string {
  if (!d) return 'Waiting for first sync';
  const diffSec = Math.max(0, Math.floor((nowMs - d.getTime()) / 1000));
  if (diffSec < 5) return 'Updated just now';
  if (diffSec < 60) return `Updated ${diffSec}s ago`;
  const mins = Math.floor(diffSec / 60);
  return `Updated ${mins}m ago`;
}

function buildHeatSignature(event: LiveEvent): string {
  const resultSig = (event.results || [])
    .map((r) => `${r.place ?? ''}|${r.lane}|${r.time ?? ''}|${r.delta_time ?? ''}|${r.wind ?? ''}`)
    .join(';');
  const startlistSig = (event.startlist || [])
    .map((r) => `${r.lane}|${r.bib ?? ''}|${r.last_name ?? ''}|${r.first_name ?? ''}`)
    .join(';');
  return `${resultSig}::${startlistSig}`;
}

/** Safely format wind (API may return number or string). Returns null if invalid. */
function formatWind(wind: number | string | null | undefined): string | null {
  const n = typeof wind === 'number' ? wind : parseFloat(String(wind ?? ''));
  if (wind == null || typeof n !== 'number' || Number.isNaN(n)) return null;
  return (n >= 0 ? '+' : '') + n.toFixed(2);
}

/** Tracks the viewport width and re-renders on resize. */
const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
};

const Live: React.FC = () => {
  const isMobile = useWindowWidth() < 640;
  const [competitions, setCompetitions] = useState<LiveCompetition[]>([]);
  const [eventMap, setEventMap] = useState<EventMap>({});
  const [pollStatus, setPollStatus] = useState<'idle' | 'ok' | 'paused' | 'error'>('idle');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPanels, setOpenPanels] = useState<string[]>([]);
  const [openDayKeys, setOpenDayKeys] = useState<string[]>([]);
  const [openEventGroupKeys, setOpenEventGroupKeys] = useState<string[]>([]);
  const [openHeatKeys, setOpenHeatKeys] = useState<string[]>([]);
  const [latestHeatUpdateId, setLatestHeatUpdateId] = useState<string | null>(null);
  const [updatedHeatIds, setUpdatedHeatIds] = useState<Record<string, number>>({});
  const [tickNow, setTickNow] = useState<number>(Date.now());

  const wsRef = useRef<WebSocket | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rateLimitedUntilRef = useRef<number>(0);
  // Tracks whether the one-time auto-open on first load has fired.
  // Using a ref so it never triggers a re-render and is immune to state resets.
  const autoOpenedRef = useRef<boolean>(false);

  const loadCompetitions = useCallback(async () => {
    if (Date.now() < rateLimitedUntilRef.current) {
      setPollStatus('paused');
      return [];
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/live/competitions`);
      if (res.status === 429) {
        rateLimitedUntilRef.current = Date.now() + 60_000;
        setPollStatus('paused');
        return [];
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const comps: LiveCompetition[] = data.competitions || [];
      setCompetitions(comps);
      setOpenPanels(prev => {
        const activeIds = comps.filter(c => c.is_active).map(c => String(c.id));
        const merged = Array.from(new Set([...prev, ...activeIds]));
        return merged.length > 0 ? merged : comps.slice(0, 1).map(c => String(c.id));
      });
      setLoading(false);
      setError(null);
      setPollStatus('ok');
      return comps;
    } catch (e: any) {
      setLoading(false);
      setError(e.message);
      setPollStatus('error');
      return [];
    }
  }, []);

  const loadEvents = useCallback(async (compId: number) => {
    if (Date.now() < rateLimitedUntilRef.current) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/live/competitions/${compId}/events`);
      if (res.status === 429) {
        rateLimitedUntilRef.current = Date.now() + 60_000;
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const events: LiveEvent[] = data.events || [];
      const changedHeatIds: string[] = [];
      const prevEvents = eventMap[compId]?.events ?? [];
      const prevById = new Map(prevEvents.map((ev) => [ev.id, ev]));
      events.forEach((ev) => {
        const prev = prevById.get(ev.id);
        if (!prev || buildHeatSignature(prev) !== buildHeatSignature(ev)) {
          changedHeatIds.push(String(ev.id));
        }
      });
      setEventMap(prev => ({
        ...prev,
        [compId]: { events, lastUpdated: new Date() },
      }));
      if (changedHeatIds.length > 0) {
        const expiresAt = Date.now() + 8_000;
        setUpdatedHeatIds((prev) => {
          const next = { ...prev };
          changedHeatIds.forEach((id) => { next[id] = expiresAt; });
          return next;
        });
        setLatestHeatUpdateId(changedHeatIds[0]);
      }
      // Auto-open the first day, event group and heat — only once on initial load.
      // After that, all expand/collapse state is owned by the user and never touched by polls.
      if (!autoOpenedRef.current && events.length > 0) {
        autoOpenedRef.current = true;
        const firstEvent = events[0];
        setOpenDayKeys(firstEvent?.event_date ? [firstEvent.event_date] : []);
        setOpenEventGroupKeys(firstEvent ? [`${firstEvent.event_no}||${firstEvent.event_name ?? ''}`] : []);
        setOpenHeatKeys(firstEvent?.id != null ? [String(firstEvent.id)] : []);
      }
    } catch (e: any) {
      console.error(`Failed to load events for competition ${compId}:`, e.message);
    }
  }, [eventMap]);

  const refreshAll = useCallback(async () => {
    const comps = await loadCompetitions();
    await Promise.all(comps.map(c => loadEvents(c.id)));
  }, [loadCompetitions, loadEvents]);

  const connectWS = useCallback(() => {
    // Only attempt WS if an explicit agent URL is configured.
    // Without it we rely on HTTP polling — no console errors for regular visitors.
    const wsURL = process.env.REACT_APP_AGENT_WS_URL;
    if (!wsURL) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      const ws = new WebSocket(wsURL);
      wsRef.current = ws;
      ws.onopen = () => {};
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'event_complete' || msg.type === 'result_row') {
            setOpenPanels(panels => {
              panels.forEach(id => loadEvents(Number(id)));
              return panels;
            });
          } else if (msg.type === 'competition_switch') {
            loadCompetitions();
          }
        } catch { /* non-JSON message */ }
      };
      ws.onclose = () => { setTimeout(connectWS, 5000); };
      ws.onerror = () => ws.close();
    } catch { /* WS unavailable — polling fallback handles updates */ }
  }, [loadEvents, loadCompetitions]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      const fresh = await loadCompetitions();
      for (const c of fresh) {
        await loadEvents(c.id);
        if (fresh.length > 1) await new Promise(r => setTimeout(r, 800));
      }
    }, POLL_INTERVAL_MS);
  }, [loadCompetitions, loadEvents]);

  useEffect(() => {
    refreshAll();
    startPolling();
    connectWS();
    return () => {
      wsRef.current?.close();
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setTickNow(now);
      setUpdatedHeatIds((prev) => {
        const next: Record<string, number> = {};
        Object.entries(prev).forEach(([k, expiresAt]) => {
          if (expiresAt > now) next[k] = expiresAt;
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handlePanelChange = (keys: string | string[]) => {
    const next = Array.isArray(keys) ? keys : [keys];
    setOpenPanels(next);
    next.forEach(id => {
      const numId = Number(id);
      if (!eventMap[numId]) loadEvents(numId);
    });
  };

  const startlistColumns = [
    { title: 'Lane', dataIndex: 'lane', key: 'lane', width: 64 },
    { title: 'Bib', dataIndex: 'bib', key: 'bib', width: 64 },
    {
      title: 'Athlete', key: 'athlete',
      render: (_: any, r: StartlistEntry) => (
        <Space direction="vertical" size={0}>
          <Text>{[r.last_name, r.first_name].filter(Boolean).join(', ')}</Text>
          {r.affiliation && <Text type="secondary" style={{ fontSize: 12 }}>{r.affiliation}</Text>}
        </Space>
      ),
    },
  ];

  const MEDAL_ROW_STYLES: Record<number, React.CSSProperties> = {
    1: { background: 'rgba(212, 175, 55, 0.15)', fontWeight: 700 },
    2: { background: 'rgba(168, 169, 173, 0.12)', fontWeight: 700 },
    3: { background: 'rgba(205, 127, 50, 0.12)', fontWeight: 700 },
  };

  const resultsColumns = [
    {
      title: 'Pl', dataIndex: 'place', key: 'place', width: 52,
      render: (p: number | null) => {
        const medalColor = p === 1 ? 'var(--racing-red)' : p === 2 ? 'var(--blue-bell)' : p === 3 ? 'var(--bright-sky)' : undefined;
        return (
          <Text strong style={{ fontSize: p != null && p <= 3 ? 16 : 14, color: medalColor }}>
            {p ?? '—'}
          </Text>
        );
      },
    },
    { title: 'Lane', dataIndex: 'lane', key: 'lane', width: 60 },
    { title: 'Bib', dataIndex: 'bib', key: 'bib', width: 60 },
    {
      title: 'Athlete', key: 'athlete',
      render: (_: any, r: ResultEntry) => (
        <Space direction="vertical" size={0}>
          <Text strong>{[r.last_name, r.first_name].filter(Boolean).join(', ')}</Text>
          {r.affiliation && <Text type="secondary" style={{ fontSize: 12 }}>{r.affiliation}</Text>}
        </Space>
      ),
    },
    {
      title: 'Result', key: 'time',
      render: (_: any, r: ResultEntry) => (
        <Space>
          <Text strong style={{ fontFamily: 'monospace', fontSize: 15 }}>{r.time || '—'}</Text>
          {(() => {
            const w = parseWind(r.wind);
            const label = formatWind(r.wind);
            return w != null && label != null && (
              <Tooltip title="Wind (m/s)">
                <Tag color={Math.abs(w) > 2 ? 'red' : 'blue'}>
                  {label}
                </Tag>
              </Tooltip>
            );
          })()}
          {r.delta_time && r.delta_time !== '0.00' && (
            <Text type="secondary" style={{ fontSize: 12 }}>{r.delta_time}</Text>
          )}
        </Space>
      ),
    },
  ];

  const renderHeatContent = (ev: LiveEvent) => {
    const hasResults = ev.results?.length > 0;
    const hasStartlist = ev.startlist?.length > 0;

    if (hasResults) {
      const sorted = [...ev.results].sort((a, b) => {
        if (a.place == null && b.place == null) return 0;
        if (a.place == null) return 1;
        if (b.place == null) return -1;
        return a.place - b.place;
      });

      if (isMobile) {
        return (
          <div>
            {sorted.map((r, i) => {
              const medalColor = r.place === 1 ? 'var(--racing-red)' : r.place === 2 ? 'var(--blue-bell)' : r.place === 3 ? 'var(--bright-sky)' : undefined;
              const rowStyle: React.CSSProperties = {
                ...(r.place != null && r.place <= 3 ? MEDAL_ROW_STYLES[r.place] : {}),
                padding: '10px 12px',
                borderBottom: `1px solid ${DARK_BORDER}`,
              };
              return (
                <div key={r.lane ?? i} style={rowStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                      <Text strong style={{ fontSize: r.place != null && r.place <= 3 ? 17 : 14, color: medalColor, flexShrink: 0, minWidth: 20 }}>
                        {r.place ?? '—'}
                      </Text>
                      <Text strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[r.last_name, r.first_name].filter(Boolean).join(', ') || '—'}
                      </Text>
                    </div>
                    <Text strong style={{ fontFamily: 'monospace', fontSize: 15, flexShrink: 0 }}>
                      {r.time || '—'}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {[r.affiliation, r.lane ? `Lane ${r.lane}` : null].filter(Boolean).join('  ·  ')}
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {(() => {
                        const w = parseWind(r.wind);
                        const label = formatWind(r.wind);
                        return w != null && label != null && (
                          <Tag color={Math.abs(w) > 2 ? 'red' : 'blue'} style={{ margin: 0, fontSize: 11 }}>
                            {label}
                          </Tag>
                        );
                      })()}
                      {r.delta_time && r.delta_time !== '0.00' && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{r.delta_time}</Text>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      return (
        <Table
          size="small"
          rowKey="lane"
          columns={resultsColumns}
          dataSource={sorted}
          pagination={false}
          rowClassName={(r: ResultEntry) => r.place != null && r.place <= 3 ? `medal-row-${r.place}` : ''}
          onRow={(r: ResultEntry) => ({ style: r.place != null && r.place <= 3 ? MEDAL_ROW_STYLES[r.place] : {} })}
        />
      );
    }

    if (hasStartlist) {
      if (isMobile) {
        return (
          <div>
            {ev.startlist.map((r, i) => (
              <div key={r.lane ?? i} style={{ padding: '10px 12px', borderBottom: `1px solid ${DARK_BORDER}` }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                  <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>Lane {r.lane}</Text>
                  {r.bib && <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>Bib {r.bib}</Text>}
                </div>
                <div style={{ marginTop: 2 }}>
                  <Text strong>{[r.last_name, r.first_name].filter(Boolean).join(', ') || '—'}</Text>
                  {r.affiliation && (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{r.affiliation}</Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }

      return (
        <Table
          size="small"
          rowKey="lane"
          columns={startlistColumns}
          dataSource={ev.startlist}
          pagination={false}
        />
      );
    }

    return <Empty description="No data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  const renderEventGroups = (groups: EventGroup[]) =>
    groups.map((group) => {
      const groupKey = `${group.event_no}||${group.event_name}`;
      const hasAnyResults = group.heats.some(h => h.results?.length > 0);
      const heatItems = group.heats.map((ev) => ({
        key: String(ev.id),
        label: (
          <Space>
            {ev.heat_number > 0
              ? <Tag>{ev.round_label ? `${ev.round_label} ${ev.heat_number}` : `Heat ${ev.heat_number}`}</Tag>
              : <Tag>{ev.round_label || 'Final'}</Tag>
            }
            {ev.results?.length > 0
              ? <Tag color="success">Results</Tag>
              : <Tag color="default">Startlist</Tag>
            }
            {updatedHeatIds[String(ev.id)] && <Tag className="heat-new-chip">New</Tag>}
          </Space>
        ),
        children: renderHeatContent(ev),
      }));

      return {
        key: groupKey,
        label: (
          <Space>
            <Tag style={{ fontWeight: 600 }}>{group.event_no}</Tag>
            <Text strong>{group.event_name || 'Event'}</Text>
            {hasAnyResults
              ? <Tag color="success">Results</Tag>
              : <Tag color="default">Startlist</Tag>
            }
          </Space>
        ),
        children: group.heats.length === 1 ? (
          renderHeatContent(group.heats[0])
        ) : (
          <Collapse
            activeKey={openHeatKeys}
            onChange={(keys) => setOpenHeatKeys(Array.isArray(keys) ? keys : [keys])}
            items={heatItems}
            size="small"
            style={{ marginLeft: isMobile ? 0 : 8, background: DARK_BG }}
          />
        ),
      };
    });

  const renderCompetitionPanel = (comp: LiveCompetition) => {
    const data = eventMap[comp.id];
    const events = data?.events ?? [];
    const lastUpdated = data?.lastUpdated;

    const agentBadge = comp.agent_online ? (
      <Tooltip title="Agent is connected and streaming live">
        <Badge status="processing" text={<Text style={{ color: 'var(--bright-sky)', fontSize: 12 }} strong>Agent live</Text>} />
      </Tooltip>
    ) : comp.is_active ? (
      <Tooltip title="Competition is active but agent has not checked in recently">
        <Badge status="warning" text={<Text style={{ color: 'var(--racing-red)', fontSize: 12 }}>Agent offline</Text>} />
      </Tooltip>
    ) : (
      <Tooltip title="No agent connected — showing archived data">
        <Badge status="default" text={<Text type="secondary" style={{ fontSize: 12 }}>Archived</Text>} />
      </Tooltip>
    );

    const header = (
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '4px 12px', width: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', minWidth: 0 }}>
          <Text strong style={{ fontSize: 15 }}>{comp.name}</Text>
          {comp.competition_date && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined /> {comp.competition_date}
            </Text>
          )}
          {agentBadge}
        </div>
        {lastUpdated && (
          <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </Text>
        )}
      </div>
    );

    return (
      <Panel header={header} key={String(comp.id)}>
        {!data ? (
          <Spin tip="Loading results…" style={{ display: 'block', padding: 24 }} />
        ) : events.length === 0 ? (
          <Empty description="No results yet — waiting for FinishLynx data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          (() => {
            const dayGroups = groupByDayAndEvent(events);
            const dayItems = dayGroups.map(([dateStr, groups]) => ({
              key: dateStr,
              label: (
                <Space>
                  <CalendarOutlined style={{ color: 'var(--text-primary)' }} />
                  <Text strong style={{ fontSize: 15, color: 'var(--text-primary)' }}>
                    {formatDayLabel(dateStr)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({groups.length} event{groups.length !== 1 ? 's' : ''})
                  </Text>
                </Space>
              ),
              children: (
                <Collapse
                  activeKey={openEventGroupKeys}
                  onChange={(keys) => setOpenEventGroupKeys(Array.isArray(keys) ? keys : [keys])}
                  items={renderEventGroups(groups)}
                  style={{ background: DARK_SURFACE }}
                />
              ),
            }));
            return (
              <Collapse
                activeKey={openDayKeys}
                onChange={(keys) => setOpenDayKeys(Array.isArray(keys) ? keys : [keys])}
                items={dayItems}
                style={{ background: 'transparent', border: `1px solid ${DARK_BORDER}`, borderRadius: 8 }}
              />
            );
          })()
        )}
      </Panel>
    );
  };

  const activeCount = competitions.filter(c => c.is_active).length;
  const latestUpdate = Object.values(eventMap)
    .map((d) => d.lastUpdated)
    .filter((d): d is Date => Boolean(d))
    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;

  const statusPill = pollStatus === 'ok'
    ? { className: 'live-badge-pill live-badge-pill--ok', label: <><WifiOutlined /> Live polling</> }
    : pollStatus === 'paused'
      ? { className: 'live-badge-pill live-badge-pill--paused', label: 'Rate-limited pause' }
      : pollStatus === 'error'
        ? { className: 'live-badge-pill live-badge-pill--error', label: <><DisconnectOutlined /> Connection issue</> }
        : { className: 'live-badge-pill live-badge-pill--idle', label: 'Connecting...' };

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorBgBase: DARK_BG,
          colorBgContainer: DARK_SURFACE,
          colorBorder: DARK_BORDER,
          colorBorderSecondary: DARK_BORDER,
          colorSplit: DARK_BORDER,
          colorText: 'var(--text-primary)',
          colorTextSecondary: 'var(--text-secondary)',
          colorPrimary: 'var(--blue-bell)',
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(42, 40, 37, 0.05)',
          boxShadowSecondary: '0 1px 4px rgba(42, 40, 37, 0.04)',
        },
      }}
    >
      <div className="live-page-shell">
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <a
            href="/"
            className="live-home-brand-link"
            aria-label="Back to TrackiAI home"
          >
            <img
              src="/logo512.png"
              alt=""
              width={32}
              height={32}
              style={{ borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
            <span className="live-home-brand-text">TrackiAI</span>
          </a>

          <div className="live-status-bar">
            <Row align="middle" justify="space-between" wrap style={{ gap: 8 }}>
              <Col>
                <Space wrap>
                  <span className={statusPill.className}>{statusPill.label}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatRelativeTime(latestUpdate, tickNow)}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Space>
                  {latestHeatUpdateId && (
                    <Button
                      size="small"
                      onClick={() => setOpenHeatKeys([latestHeatUpdateId])}
                    >
                      Jump to latest update
                    </Button>
                  )}
                  <Button icon={<ReloadOutlined />} size="small" onClick={refreshAll}>
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          <Row align="middle" justify="space-between" wrap style={{ marginBottom: 20, gap: 8 }}>
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <ThunderboltOutlined style={{ color: 'var(--bright-sky)', marginRight: 8 }} />
                Live Results
              </Title>
            </Col>
            <Col>
              <Space>
                {pollStatus === 'ok' ? (
                  <Tooltip title="Polling every 15 s — data is current">
                    <Badge status="processing" text={<Text style={{ fontSize: 12 }}><WifiOutlined /> Polling</Text>} />
                  </Tooltip>
                ) : pollStatus === 'paused' ? (
                  <Tooltip title="Rate limited — polling paused for ~60 s">
                    <Badge status="warning" text={<Text style={{ fontSize: 12 }}>Paused</Text>} />
                  </Tooltip>
                ) : pollStatus === 'error' ? (
                  <Tooltip title="Failed to reach server — check your connection">
                    <Badge status="error" text={<Text style={{ fontSize: 12 }}><DisconnectOutlined /> Error</Text>} />
                  </Tooltip>
                ) : (
                  <Badge status="default" text={<Text style={{ fontSize: 12 }}>Connecting…</Text>} />
                )}
              </Space>
            </Col>
          </Row>

          {activeCount > 1 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${activeCount} competitions are currently streaming live`}
              description="Results from all of them appear below. Click a competition header to expand or collapse it."
            />
          )}

          {error && (
            <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />
          )}

          <Spin spinning={loading}>
            {competitions.length === 0 && !loading ? (
              <Empty
                description="There are no ongoing competitions"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Collapse
                activeKey={openPanels}
                onChange={handlePanelChange}
                expandIconPosition="end"
                style={{ background: 'transparent', border: 'none' }}
                ghost
              >
                {competitions.map(comp => renderCompetitionPanel(comp))}
              </Collapse>
            )}
          </Spin>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Live;
